import { RoomWebsocketErrorCode } from './room-error-code';

/**
 * App错误类型
 */
export enum SlideAppError {
    /**
     * 无错误
     */
    None = 0,
    /**
     * 未知错误
     */
    Unknown,
    /**
     * 服务器错误
     */
    ServerError,
    /**
     * 连接超时
     */
    Timeout,
    /**
     * 登录态失效
     */
    LoginExpired,
    /**
     * 权限失效
     */
    AuthExpired,
    /**
     * 包过大
     */
    SizeLimit,
    /**
     * 前端数据错误
     */
    DataError,
    /**
     * 安全打击
     */
    SecurityStrike,
    /**
     * 房间满员
     */
    FullRoom,
    /**
     * 主动cancel
     */
    Cancel,
}

export enum ServerErrorCode {
    /**
     * 重复提交
     */
    COMMIT_DUPLICATE = 604460,
    /**
     * 无提交权限
     */
    COMMIT_NO_AUTH = 6044103,
    /**
     * 前端提交baserev跟后台最新版本差距200个以上
     */
    COMMIT_ERROR_BASEREV_LIMIT_ERROR = 640033,
    /**
     * 提交参数不合法
     */
    INVALID_COMMIT_ATTRS = 640028,
    /**
     * 提交数据过大
     */
    OVERSIZE_COMMIT = 640029,
    /**
     * 应用失败
     */
    APPLY_REVISION_CGO = 640035,
    /**
     * 文件不存在
     */
    META_DATA_NOT_EXIST = 604424,
    /**
     * 冲突处理失败
     */
    CGO_TRANSFORM = 604488,
    /**
     * 正在发送的cs被取消
     */
    COMMIT_ERROR_CANCEL_SENDING = 999999,
    /**
     * 等待发送的cs被取消
     */
    COMMIT_ERROR_CANCEL_WAITING = 999998,
}

/**
 * 错误码分类
 */
export const SlideAppErrorCodeMap: Record<string, SlideAppError> = {
    0: SlideAppError.None,
    /**
     * 内部错误，请找相应后台负责人查看
     */
    [RoomWebsocketErrorCode.SOCKET_SERVER_ERROR]: SlideAppError.ServerError,
    /**
     * 接入层客户端链接不存在，前端重连
     */
    [RoomWebsocketErrorCode.SOCKET_LAYER_ERROR]: SlideAppError.ServerError,
    /**
     * 在ClientReady成功前发协同编辑数据，前端重试
     */
    [RoomWebsocketErrorCode.SOCKET_CLIENT_NOT_READY_ERROR]: SlideAppError.Timeout,
    /**
     * 客户端请求中的数据有错，前端报错
     */
    [RoomWebsocketErrorCode.SOCKET_CLIENT_DATA_ERROR]: SlideAppError.DataError,
    /**
     * 房间层后台超时，前端重试
     */
    [RoomWebsocketErrorCode.SOCKET_COLLAB_ROOM_TIMEOUT]: SlideAppError.Timeout,
    /**
     * 任务层后台超时，前端重试
     */
    [RoomWebsocketErrorCode.SOCKET_COLLAB_SERVER_TIMEOUT]: SlideAppError.Timeout,
    /**
     * 用户登录态校验失败，前端重新登录
     */
    [RoomWebsocketErrorCode.SOCKET_USER_AUTH_FAILED]: SlideAppError.LoginExpired,
    /**
     * 文档权限校验失败，前端重新刷新，对齐权限变更
     */
    [RoomWebsocketErrorCode.SOCKET_DOC_PRIVILEGE_FAILED]: SlideAppError.LoginExpired,
    /**
     * 超过最大包长限制（10M）
     */
    [RoomWebsocketErrorCode.SOCKET_PACKAGE_SIZE_TOO_LARGE]: SlideAppError.SizeLimit,
    /**
     * clientReady已在房间，前端忽略
     */
    [RoomWebsocketErrorCode.SOCKET_COLLAB_ROOM_ALREADY_EXIST]: SlideAppError.None,
    /**
     * 房间层满员，前端提示
     */
    [RoomWebsocketErrorCode.SOCKET_COLLAB_ROOM_MEMBER_FULL]: SlideAppError.FullRoom,
    /**
     * 房间存储层超时，前端重试
     */
    [RoomWebsocketErrorCode.SOCKET_SERVER_STORAGE_TIMEOUT]: SlideAppError.Timeout,
    /**
     * 接入层cgi超时，前端重试
     */
    [RoomWebsocketErrorCode.SOCKET_SERVER_CGI_TIMEOUT]: SlideAppError.Timeout,
    /**
     * 频率限制，前端稍后重试
     */
    [RoomWebsocketErrorCode.SOCKET_FREQUENCY_LIMIT]: SlideAppError.Timeout,
    /**
     * 用户文档权限校验无权限，前端重新刷新，对齐权限变更
     */
    [RoomWebsocketErrorCode.SOCKET_USER_DOC_NOPRIVILEGE]: SlideAppError.AuthExpired,
    /**
     * userchange参数错误
     */
    [RoomWebsocketErrorCode.SOCKET_USER_CHANGE_PARAM_ERROR]: SlideAppError.DataError,
    /**
     * 任务管理层发现不支持命令
     */
    [RoomWebsocketErrorCode.SOCKET_COMMAND_NOT_SUPPORTED]: SlideAppError.DataError,
    /**
     * commit error：Hackpad调用超时，前端超时
     */
    [RoomWebsocketErrorCode.SOCKET_HACKPAD_TIMEOUT]: SlideAppError.Timeout,
    /**
     * onpost：队列超长，前端延迟重试
     */
    [RoomWebsocketErrorCode.SOCKET_SERVER_WAITTING_TOO_LONG]: SlideAppError.Timeout,
    /**
     * commit error：队列超时，前端延迟重试
     */
    [RoomWebsocketErrorCode.SOCKET_SERVER_WAITTING_TIMEOUT]: SlideAppError.Timeout,
    /**
     * ERR_AUTH__TIMEOUT 鉴权超时，前端延迟重试
     */
    [RoomWebsocketErrorCode.SOCKET_AUTH_TIMEOUT]: SlideAppError.Timeout,
    /**
     * ERR_GET_PRIVILEGE_TIMEOUT 获取权限超时，前端延迟重试
     */
    [RoomWebsocketErrorCode.SOCKET_GET_PRIVILEGE_TIMEOUT]: SlideAppError.Timeout,
    /**
     * COMMIT_ERROR_EXCEPTION, 内部错误，请找相应后台负责人查看
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_SERVER_ERROR]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_PAD_NOT_EXISTED, 文档不存在
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_DOC_NOT_EXIST]: SlideAppError.DataError,
    /**
     * COMMIT_ERROR_SUBID_ERROR, subId不正确
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_SUBID_ERROR]: SlideAppError.DataError,
    /**
     * COMMIT_ERROR_CS_CHECK_FAILED, 提交失败（action检查失败：长度不对，回车换行符不对）
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_ACTION_LENGTH]: SlideAppError.DataError,
    /**
     * COMMIT_ERROR_NO_WRITE_RIGHT, 没有写权限
     */
    40004: SlideAppError.AuthExpired,
    /**
     * COMMIT_ERROR_FOLLOW_FAILED, 冲突处理错误
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_CHANGESET_EASYSYNC]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_SAVE_FAILED, 数据落盘失败
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_SAVE_FAIL]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_CHANGESET_NULL, changeset空
     */
    40007: SlideAppError.DataError,
    /**
     * COMMIT_ERROR_READ_FAILED, 读数据失败
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_READ_FAIL]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_BASEREV_ERROR, baseRev错误
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_BASEREV_ERROR]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_PAD_TYPE_ERROR, 不支持文档类型
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_NOT_SUPPORTED_TYPE]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_NOT_SUPPORT_MSG, 不支持请求协议
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_NOT_SUPPORTED_PROTO]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_CODEREV_ERROR, 灰度版本不一致
     */
    [RoomWebsocketErrorCode.SOCKET_GRAY_VERSION_INCONFORMITY]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_BLACK_ERROR, 安全打击了
     */
    [RoomWebsocketErrorCode.SOCKET_DOCUMENT_EDIT_COLLAB_LIMIT]: SlideAppError.SecurityStrike,
    /**
     * COMMIT_ERROR_CS_LIMIT_ERROR, 编辑提交大小限制
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_CONTENT_LIMIT]: SlideAppError.SizeLimit,
    /**
     * 前端提交baserev跟后台最新版本差距10
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_REV_DIFF_UPPER_TEN]: SlideAppError.DataError,
    /**
     * COMMIT_ERROR_FOLLOW_TIMEOUT_ERROR, 当前提交后台处理协同冲突超时
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_EASYSYNC_TIMEOUT]: SlideAppError.SizeLimit,
    /**
     * COMMIT_ERROR_CLIENTREADY_LOW_RELEASE_ERROR CLIENT_READY 前端js版本过期
     */
    [RoomWebsocketErrorCode.SOCKET_CLIENT_VERSION_TOO_OLD]: SlideAppError.ServerError,
    /**
     * COMMIT_ERROR_CHANGES_LOW_RELEASE_ERROR USER_CHANGES 前端js版本过期
     */
    [RoomWebsocketErrorCode.SOCKET_JS_TOO_OLD]: SlideAppError.DataError,
    /**
     * 前端限制没有冲突编辑提交，（存在冲突）
     */
    [RoomWebsocketErrorCode.SOCKET_NOT_CONFILCT_TO_COMMIT]: SlideAppError.DataError,
    /**
     * 后台发生异常错误
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_SERVER_UNKNOWN_ERROR]: SlideAppError.ServerError,
    /**
     * ERROR_MISS_PARAM_VALUE, 数据协议字段缺失
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_PROTOCOL_FIELD_LACK]: SlideAppError.ServerError,
    /**
     * BATCH_DOCID_NOT_EQUAL, 批量提交docid不一致
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_DOCID_ERROR]: SlideAppError.ServerError,
    /**
     * BATCH_TYPE_NOT_EQUAL, 批量提交协议类型不一致
     */
    [RoomWebsocketErrorCode.SOCKET_DATA_PROTOCOL_TYPE_ERROR]: SlideAppError.ServerError,
    /**
     * 非会员情况，房间层满员，前端提示
     */
    [RoomWebsocketErrorCode.SOCKET_COLLABROOM_NOT_VIP_FULL]: SlideAppError.FullRoom,
    /**
     * Websocket 提交消息超时
     */
    [RoomWebsocketErrorCode.SOCKET_SEND_MESSAGE_TIMEOUT]: SlideAppError.Timeout,
    /**
     * 表示登录态失效
     */
    [RoomWebsocketErrorCode.LOGIN_INVALIDATE]: SlideAppError.LoginExpired,
    /**
     * 表示登录限制
     */
    [RoomWebsocketErrorCode.LOGIN_FORBIDDEN]: SlideAppError.LoginExpired,
    /**
     * 后台房间踢人，原因是不活跃或者数据升级类
     */
    [RoomWebsocketErrorCode.INACTIVE]: SlideAppError.Timeout,
    /**
     * 重复提交
     */
    [ServerErrorCode.COMMIT_DUPLICATE]: SlideAppError.Timeout,
    /**
     * 无提交权限
     */
    [ServerErrorCode.COMMIT_NO_AUTH]: SlideAppError.AuthExpired,
    /**
     * COMMIT_ERROR_BASEREV_LIMIT_ERROR, 前端提交baserev跟后台最新版本差距200个以上
     */
    [ServerErrorCode.COMMIT_ERROR_BASEREV_LIMIT_ERROR]: SlideAppError.DataError,
    /**
     * 提交参数不合法
     */
    [ServerErrorCode.INVALID_COMMIT_ATTRS]: SlideAppError.DataError,
    /**
     * OVERSIZE_COMMIT 提交数据过大
     */
    [ServerErrorCode.OVERSIZE_COMMIT]: SlideAppError.SizeLimit,
    /**
     * 应用失败
     */
    [ServerErrorCode.APPLY_REVISION_CGO]: SlideAppError.ServerError,
    /**
     * 文件不存在
     */
    [ServerErrorCode.META_DATA_NOT_EXIST]: SlideAppError.ServerError,
    /**
     * 冲突处理失败
     */
    [ServerErrorCode.CGO_TRANSFORM]: SlideAppError.ServerError,
    /**
     * 主动cancel当前正在发送中的changeset
     */
    [ServerErrorCode.COMMIT_ERROR_CANCEL_SENDING]: SlideAppError.Cancel,
    /**
     * 主动cancel
     */
    [ServerErrorCode.COMMIT_ERROR_CANCEL_WAITING]: SlideAppError.Cancel,
};

export enum SlideError {
    // page: 预留13000至13020
    SLIDE_ERROR_BASE = 13000,
    SLIDE_ERROR_UNKNOWN = 13001,
    SLIDE_ERROR_MUT_INVALID = 13002, // MUT自身错误
    SLIDE_ERROR_PAGE_NOT_FOUND = 13003, // page不存在
    SLIDE_ERROR_PAGE_NOT_LOADED = 13004, // page没加载
    SLIDE_ERROR_LAYOUT_NOT_BELONG_TO_MASTER = 13005, // layout不属于master
    SLIDE_ERROR_PAGE_POSITION_OUT_OF_RANGE = 13006, // 页位置不合法
    SLIDE_ERROR_PAGE_INSERT_POSITION_OUT_OF_RANGE = 13007, // 插入页位置不合法
    SLIDE_ERROR_PAGE_NOT_IN_POSITION = 13008, // 页不在该位置
    SLIDE_ERROR_PAGE_SHAPE_TREE_NOT_EXIST = 13009, // 页不存在shape_tree

    // text: 预留13021至13040
    SLIDE_ERROR_CONVERT_TO_TEXTBODY = 13021, // 无法转换成 TextBody 类型
    SLIDE_ERROR_INVALID_TEXT_CONTENT = 13022, // 不合规范的文本内容
    SLIDE_ERROR_INVALID_RUN_PROPERTIES = 13023, // 不合规的 Run 属性变更
    SLIDE_ERROR_INVALID_PARAGRAPH_PROPERTIES = 13024, // 不合规的 paragraph 属性变更
    SLIDE_ERROR_INVALID_FIELD_PROPERTIES = 13025, // 不合规的 field 属性变更
    SLIDE_ERROR_INVALID_TEXT_PROPERTIES = 13026, // Mutation 中属性数据不合法
    SLIDE_ERROR_INVALID_PROPERTY_UPDATE = 13027, // 不支持的属性变更
    SLIDE_ERROR_INVALID_TEXT_OPERATION_POSITION = 13028, // 文本操作的位置不合法

    // shape: 预留13041-13060
    SLIDE_ERROR_UNKNOWN_SHAPE_TYPE = 13041, // 不合法的shape类型
    SLIDE_ERROR_SHAPE_NOT_FOUND_IN_PAGE = 13042, // page中找不到shape
    SLIDE_ERROR_SHAPE_NOT_FOUND = 13043, // shape 不存在
    SLIDE_ERROR_SHAPE_NOT_IN_PAGE = 13044, // shape不在此页中 TODO(xiaoyewang): 这个应该用不到, 前端有用到先加上
    SLIDE_ERROR_SHAPE_NOT_CHILD_OF_SHAPE_TREE = 13045, // slide不是shape_tree的子页
    SLIDE_ERROR_SHAPE_TYPE_NOT_MATCH = 13046, // shape类型不匹配
    SLIDE_ERROR_SHAPE_INSERT_POS_OUT_OF_RANGE = 13047, // shape插入位置不合法
    SLIDE_ERROR_SHAPE_NOTE_FOUND_EXIST = 13048, // TODO(maopeng): 解释
    SLIDE_ERROR_SHAPE_NOT_IN_POSITION = 13049, // shape不在此位置
    SLIDE_ERROR_SHAPE_NOT_CHILD_OF_GROUP_SHAPE = 13050, // shape不是group的子shape
    SLIDE_ERROR_SHAPE_PLACEHOLDER_NOT_EXIST = 13051, // shape占位符不存在
    SLIDE_ERROR_SHAPE_INVALIDE_TYPE = 13052, // shape type 不合法
    SLIDE_ERROR_SHAPE_ALREADY_EXISTS = 13053, // shape已经存在

    // chart:预留13061到13070作为图表错误处理
    SLIDE_ERROR_SUB_CHART_INDEX_OUT_OF_BOUNDS = 13061, // 子表索引越界
    SLIDE_ERROR_CHART_NOT_FOUND = 13062, // 图表未找到
    SLIDE_ERROR_SUB_CHART_POSITION_OUT_OF_RANGE = 13063, // 子表越界
    SLIDE_ERROR_SUB_CHART_NOT_FOUND = 13064, // 子表未找到
    SLIDE_ERROR_SUB_CHART_TYPE_NOT_MATCH = 13065, // 子表类型不匹配

    // table: 预留13071到13090
    SLIDE_ERROR_TABLE_MUT_OUT_OF_RANGE = 13071, // table-mut非法范围
    SLIDE_ERROR_MUT_EXCEED_TABLE_LIMIT = 13072, // table超出限制
    SLIDE_ERROR_MUT_EMPTY_TABLE = 13073, // table为空
    SLIDE_ERROR_MUT_UNMERGE_TABLE_RANGE_INVALID = 13074, // unmerge操作的范围不合法
    SLIDE_ERROR_MUT_MERGE_TABLE_RANGE_INVALID = 13075, // merge操作的范围不合法
    SLIDE_ERROR_MUT_TABLE_NOT_FOUND = 13076, // table不存在
    SLIDE_ERROR_MUT_TABLE_RANGE_INVALID = 13077, // table-range invalid

    // animation: 预留13081到13090
    SLIDE_ERROR_TIMING_SEQ_NOT_FOUND = 13081, // timing seq找不到
    SLIDE_ERROR_UNIT_TIME_NODE_POSITION_OUT_OF_RANGE = 13082, // node位置范围非法
    SLIDE_ERROR_UNIT_TIME_NODE_INSERT_POSITION_OUT_OF_RANGE = 13083, // node插入位置范围发发

    // section/theme/resource: 13091开始
    SLIDE_ERROR_SECTION_NOT_FOUND = 13091, // section找不到
    SLIDE_ERROR_SECTION_BINDING_SLIDE_INDEX_OUT_OF_RANGE = 13092, // section关联的slide index不合法
    SLIDE_ERROR_SECTION_INSERT_POSITION_OUT_OF_RANGE = 13093, // section插入位置不合法
    SLIDE_ERROR_THEME_HAS_EXISTED = 13094, // 主题已经存在
    SLIDE_ERROR_THEME_USED = 13095, // 主题已被master使用
    SLIDE_ERROR_THEME_NOT_EXIST = 13096, // 主题不存在
    SLIDE_ERROR_RESOURCE_TYPE_NOT_MATCH = 13097, // 资源类型不匹配
    SLIDE_ERROR_SECTION_ALREADY_EXISTS = 13098, // section 已存在

    // transform: 预留13111到13120
    SLIDE_ERROR_INVALID_TRANSFORM_ARGUMENT = 13111, // transform参数问题
    SLIDE_ERROR_NO_SHAPE_TO_COMPUTE_TRANSFORM = 13112, // 缺少子元素无法动态计算 transform
}

export const SLIDE_ERROR_VALUES = Object.values(SlideError).filter((v) => typeof v === 'number');
SLIDE_ERROR_VALUES.forEach((v) => {
    SlideAppErrorCodeMap[v.toString()] = SlideAppError.DataError;
});

/**
 * 根据错误码获取错误类型
 * @param code 错误码
 */
export function GetSlideAppErrorByCode(code: number) {
    return SlideAppErrorCodeMap[code.toString()] ?? SlideAppError.Unknown;
}

/**
 * 拉取断层错误
 */
export enum FetchMissingChangesError {
    ResponseError = 1,
    CGIError = 2,
    ResultError = 3,
    ParamError = 4,
    Timeout = 5,
}
